import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import cone from '../assets/da2026/cone.png';

// Nouq repond en Markdown (gras, listes, tableaux de classement). Sans rendu, les
// utilisateurs voyaient les `**` en clair dans les bulles.
// Tailwind est charge par CDN sans le plugin typography : chaque balise est donc
// stylee explicitement ici plutot que via des classes `prose`.
const markdownComponents = {
  p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
  strong: ({ children }) => <strong className="font-bold text-brand-orange-deep">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  del: ({ children }) => <del className="line-through opacity-60">{children}</del>,
  ul: ({ children }) => <ul className="list-disc pl-5 mb-2 last:mb-0 space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 mb-2 last:mb-0 space-y-0.5">{children}</ol>,
  li: ({ children }) => <li className="leading-snug">{children}</li>,
  h1: ({ children }) => <h3 className="font-bold text-base mb-1.5">{children}</h3>,
  h2: ({ children }) => <h3 className="font-bold text-base mb-1.5">{children}</h3>,
  h3: ({ children }) => <h3 className="font-bold text-sm mb-1.5">{children}</h3>,
  hr: () => <hr className="my-2 border-brand-line" />,
  code: ({ children }) => (
    <code className="bg-brand-cream border border-brand-line rounded px-1 py-0.5 text-[0.85em]">{children}</code>
  ),
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noreferrer" className="text-brand-orange underline">{children}</a>
  ),
  blockquote: ({ children }) => (
    <blockquote className="border-l-2 border-brand-orange pl-3 italic opacity-90">{children}</blockquote>
  ),
  // Un classement complet deborde de l'ecran sur mobile : le tableau scrolle
  // horizontalement dans sa bulle au lieu de pousser toute la page.
  table: ({ children }) => (
    <div className="overflow-x-auto no-sb -mx-1 my-2">
      <table className="w-full text-xs border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead className="bg-brand-cream">{children}</thead>,
  th: ({ children }) => (
    <th className="border border-brand-line px-2 py-1 text-left font-bold whitespace-nowrap">{children}</th>
  ),
  td: ({ children }) => <td className="border border-brand-line px-2 py-1 align-top">{children}</td>
};

const Chat = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    {
      type: 'ai',
      content: "Egun on ! Je suis ton assistant pour le tournoi ABRF. Je suis là pour t'aider, te donner des renseignements sur les rencontres, les horaires, les classements... Pose moi n'importe quelle question ! Milesker !"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const [sessionId, setSessionId] = useState(null);

  useEffect(() => {
    const initializeChat = async () => {
      let storedSessionId = localStorage.getItem('chatSessionId');
      if (!storedSessionId) {
        storedSessionId = uuidv4();
        localStorage.setItem('chatSessionId', storedSessionId);
      }
      setSessionId(storedSessionId);

      try {
        const response = await fetch(`/api/chat/history`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: storedSessionId })
        });
        if (response.ok) {
          const data = await response.json();
          if (data.history && data.history.length > 0) {
            setMessages(data.history);
          }
        }
      } catch (error) {
        console.error("Impossible de récupérer l'historique du chat:", error);
      }
    };

    initializeChat();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setIsLoading(true);
    setError(null);

    setMessages(prev => [...prev, { type: 'human', content: userMessage }]);

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage, sessionId: sessionId })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      setMessages(prev => [...prev, {
        type: 'ai',
        content: data.response || 'Désolé, je n\'ai pas pu générer une réponse.'
      }]);
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message:', error);
      setError('Erreur lors de la communication avec le chatbot. Veuillez réessayer.');
      setMessages(prev => [...prev, {
        type: 'ai',
        content: 'Désolé, j\'ai rencontré une erreur. Veuillez réessayer votre question.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatMessage = (content) => (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {content}
    </ReactMarkdown>
  );

  const suggestions = [
    { icon: 'ri-flashlight-fill', text: 'Mon prochain match' },
    { icon: 'ri-trophy-fill', text: 'Classement' },
    { icon: 'ri-calendar-event-line', text: 'Horaires du jour' },
  ];

  return (
    <div className="max-w-md mx-auto flex flex-col" style={{ height: 'calc(100dvh - 5.5rem)' }}>
      {/* Header Nouq */}
      <header className="relative bg-gradient-to-br from-brand-orange to-brand-orange-deep text-white rounded-b-3xl overflow-hidden shadow-hero shrink-0">
        <div className="absolute top-0 inset-x-0 h-2 hazard-white"></div>
        <div className="flex items-center gap-3 px-5 pt-5 pb-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Retour"
            className="w-9 h-9 rounded-xl bg-white/15 border border-white/25 flex items-center justify-center shrink-0 active:scale-95 transition-transform"
          >
            <i className="ri-arrow-left-line text-xl"></i>
          </button>
          <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-soft shrink-0">
            <img src={cone} alt="Nouq" className="w-8 h-8 object-contain" />
          </div>
          <div className="min-w-0">
            <div className="h-display text-xl leading-none">Nouq</div>
            <div className="flex items-center gap-1.5 mt-1.5 text-white/90 text-xs font-medium">
              <span className="w-1.5 h-1.5 rounded-full bg-[#8CF0BC]"></span>
              Assistant ABRF · en ligne
            </div>
          </div>
        </div>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto no-sb bg-brand-cream px-4 py-4 space-y-4">
        {messages.map((message, index) => (
          message.type === 'human' ? (
            <div key={index} className="flex justify-end">
              {/* Le message de l'utilisateur reste du texte brut : un `*` tape au clavier
                  ne doit pas etre interprete comme du Markdown et disparaitre. */}
              <div className="max-w-[85%] bg-gradient-to-br from-brand-orange to-brand-orange-deep text-white rounded-2xl rounded-tr-md px-4 py-3 text-sm whitespace-pre-wrap">
                {message.content}
              </div>
            </div>
          ) : (
            <div key={index} className="flex gap-2 max-w-[92%]">
              <div className="w-8 h-8 rounded-xl bg-white border border-brand-line flex items-center justify-center shrink-0 mt-4">
                <img src={cone} alt="" className="w-5 h-5 object-contain" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-wider text-brand-orange-deep mb-1">Nouq</div>
                <div className="bg-white border border-brand-line rounded-2xl rounded-tl-md px-4 py-3 text-sm text-brand-ink shadow-soft">
                  {formatMessage(message.content)}
                </div>
              </div>
            </div>
          )
        ))}

        {isLoading && (
          <div className="flex gap-2">
            <div className="w-8 h-8 rounded-xl bg-white border border-brand-line flex items-center justify-center shrink-0">
              <img src={cone} alt="" className="w-5 h-5 object-contain" />
            </div>
            <div className="bg-white border border-brand-line rounded-2xl rounded-tl-md px-4 py-3 flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-brand-line border-t-brand-orange"></div>
              <span className="text-sm text-brand-muted">Nouq réfléchit…</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {error && (
        <div className="px-4 py-2 bg-brand-orange-soft border-t border-brand-orange shrink-0">
          <p className="text-brand-orange-deep text-sm">{error}</p>
        </div>
      )}

      {/* Suggestions */}
      <div className="flex gap-2 overflow-x-auto no-sb px-4 py-3 bg-brand-cream shrink-0">
        {suggestions.map((s) => (
          <button
            key={s.text}
            type="button"
            onClick={() => setInput(s.text)}
            className="shrink-0 inline-flex items-center gap-1.5 bg-white border border-brand-orange/30 text-brand-orange-deep text-xs font-semibold px-3 py-2 rounded-full"
          >
            <i className={s.icon}></i>{s.text}
          </button>
        ))}
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="flex items-center gap-2 px-4 py-3 bg-white border-t border-brand-line shrink-0">
        <div className="flex-1 flex items-center gap-2 bg-brand-cream border border-brand-line rounded-full px-4 py-3">
          <i className="ri-chat-smile-3-fill text-brand-orange"></i>
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Pose ta question…"
            disabled={isLoading}
            className="flex-1 bg-transparent outline-none text-sm text-brand-ink placeholder:text-brand-muted"
          />
        </div>
        <button
          type="submit"
          disabled={!input.trim() || isLoading}
          className="shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-brand-orange to-brand-orange-deep text-white flex items-center justify-center shadow-[0_8px_18px_rgba(217,83,26,0.4)] disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading
            ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-white/40 border-t-white"></div>
            : <i className="ri-send-plane-fill text-xl"></i>}
        </button>
      </form>
    </div>
  );
};

export default Chat;
